import { useState } from 'react';
import { sanitizeInput, validateEmail } from '../../utils/inputValidation';
import { isTauriApp } from '../../utils/tauriHelper';
import Icon from '../Common/Icon';
import './AuthForms.css';

const REMEMBER_DEVICE_STORAGE_KEY = 'thryveloop_pb_remember_device';

/**
 * LoginForm Component
 *
 * Privacy-first login form for ThryveLoop
 * - Email + password authentication
 * - Remember me option
 * - Forgot password link
 * - Error handling
 */
const LoginForm = ({
  onLogin,
  onSwitchToSignup,
  onForgotPassword,
  isLoading = false,
  error = null,
}) => {
  const initialRemember = (() => {
    if (typeof window === 'undefined') {
      return false;
    }
    try {
      const stored = window.localStorage.getItem(REMEMBER_DEVICE_STORAGE_KEY);
      if (stored === null) {
        return !isTauriApp();
      }
      return stored === '1';
    } catch (error) {
      console.warn('[LoginForm] Unable to read remember-device preference:', error);
      return false;
    }
  })();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: initialRemember,
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    let processedValue = newValue;

    if (name === 'email' && typeof newValue === 'string') {
      processedValue = sanitizeInput(newValue, { lowerCase: true });
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));

    if (name === 'rememberMe' && typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(REMEMBER_DEVICE_STORAGE_KEY, processedValue ? '1' : '0');
      } catch (storageError) {
        console.warn('[LoginForm] Failed to persist remember-device preference:', storageError);
      }
    }

    // Clear validation error for this field
    setValidationErrors(prev => ({
      ...prev,
      [name]: null,
    }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    // Email validation (shared sanitization)
    const emailResult = validateEmail(formData.email);
    if (!emailResult.valid) {
      errors.email = emailResult.error;
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return { ok: Object.keys(errors).length === 0, sanitizedEmail: emailResult.sanitized };
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const { ok, sanitizedEmail } = validateForm();
    if (!ok) {
      return;
    }

    // Call parent's onLogin handler
    await onLogin({
      email: sanitizedEmail,
      password: formData.password,
      rememberMe: formData.rememberMe,
    });
  };

  const handleGoogleLogin = async () => {
    if (onLogin.googleSignIn) {
      await onLogin.googleSignIn();
    }
  };

  return (
    <div className="auth-form-container">
      <form className="auth-form" onSubmit={handleSubmit}>
        <div className="auth-form-header">
          <h2>Welcome back</h2>
          <p className="auth-form-subtitle">
            Log in to open your ThryveLoop workspace.
          </p>
        </div>

        {error && (
          <div className="auth-error-banner" role="alert">
            {error}
          </div>
        )}

        <button
          type="button"
          className="btn-modern google-btn"
          onClick={handleGoogleLogin}
          disabled={isLoading}
        >
          <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg"><g fill="none" fillRule="evenodd"><path d="M17.6 9.2l-.1-1.8H9v3.4h4.8C13.6 12 13 13 12 13.6v2.2h3a8.8 8.8 0 0 0 2.6-6.6z" fill="#4285F4"/><path d="M9 18c2.4 0 4.5-.8 6-2.2l-3-2.2a5.4 5.4 0 0 1-8-2.9H1V13a9 9 0 0 0 8 5z" fill="#34A853"/><path d="M4 10.7a5.4 5.4 0 0 1 0-3.4V5H1a9 9 0 0 0 0 8l3-2.3z" fill="#FBBC05"/><path d="M9 3.6c1.3 0 2.5.4 3.4 1.3L15 2.3A9 9 0 0 0 1 5l3 2.4a5.4 5.4 0 0 1 5-3.7z" fill="#EA4335"/></g></svg>
          Continue with Google
        </button>

        <div className="auth-inline-divider">or</div>

        {/* Email field */}
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
            autoComplete="email"
            required
            disabled={isLoading}
            className={validationErrors.email ? 'input-error' : ''}
          />
          {validationErrors.email && (
            <span className="error-message">{validationErrors.email}</span>
          )}
        </div>

        {/* Password field */}
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
              disabled={isLoading}
              className={validationErrors.password ? 'input-error' : ''}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'eye-off' : 'eye'} size={18} strokeWidth={2.2} />
            </button>
          </div>
          {validationErrors.password && (
            <span className="error-message">{validationErrors.password}</span>
          )}
        </div>

        {/* Remember me + Forgot password */}
        <div className="form-group form-group-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
              disabled={isLoading}
            />
            <span className="remember-key-copy">
              <strong>Remember sync key on this device</strong>
              <small>Keeps this browser ready to open your encrypted records. Web access renews on a rolling 30-day window.</small>
            </span>
          </label>

          <button
            type="button"
            className="link-button"
            onClick={onForgotPassword}
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          className="btn-modern primary auth-submit-btn"
          disabled={isLoading}
        >
          {isLoading ? 'Logging In...' : 'Log In'}
        </button>

        {/* Switch to signup */}
        <div className="auth-form-footer">
          <p>
            Don't have an account?{' '}
            <button
              type="button"
              className="link-button"
              onClick={onSwitchToSignup}
              disabled={isLoading}
            >
              Sign Up
            </button>
          </p>
        </div>

        {/* Privacy notice */}
        <div className="privacy-notice">
          <p>
            <strong>Private records:</strong> Your password travels over an encrypted connection for authentication.
            Personal records use separate on-device encryption before cloud sync.
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
